import Button from '@mui/material/Button'
import ConfirmDialog from 'components/ConfirmDialog'
import { Stream, MAX_FEEDS_IN_STREAM } from 'interfaces'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styles from './MergeStreamSelect.pcss'
import Popper from '@mui/material/Popper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Paper from '@mui/material/Paper'
import { overflowModifiers } from 'styles/popover'

interface MergeStreamSelectProps {
  baseStream: Stream
  streams: Stream[]
  className?: string
  onStreamSelected: (stream: Stream) => void
}

export function MergeStreamSelect({ streams, baseStream, className, onStreamSelected }: MergeStreamSelectProps) {
  const intl = useIntl()
  const [confirmText, setConfirmText] = React.useState('')
  const [hideConfirmBtn, setHideConfirmBtn] = React.useState(false)
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null)

  const openDropdown = (event: React.MouseEvent<HTMLElement>) => {
    setAnchor(event.currentTarget)
  }

  const onBeforeConfirm = (s: Stream) => {
    const currentFeedsIds = baseStream.feeds.reduce((acc, f) => {
      acc[f.uniqueSource] = true
      return acc
    }, {} as Record<string, boolean>)
    // get unique feeds from baseStream and mergeStream
    const feedsToAdd = s.feeds.filter(f => !currentFeedsIds[f.uniqueSource])
    if (feedsToAdd.length + baseStream.feeds.length > MAX_FEEDS_IN_STREAM) {
      setHideConfirmBtn(true)
      setConfirmText(intl.formatMessage(
        { id: 'find.my-streams.manage.confirm-merge-msg-max-feeds' },
        { limit: MAX_FEEDS_IN_STREAM }
      ))
    } else {
      setHideConfirmBtn(false)
      setConfirmText(intl.formatMessage(
        { id: 'find.my-streams.manage.confirm-merge-msg' },
        {
          baseStream: baseStream.title,
          mergeStream: s.title,
          br: () => <br />
        } as any
      ))
    }
    closeDropdown()
  }

  const closeDropdown = () => {
    setAnchor(null)
  }

  const onStreamClick = (stream: Stream) => {
    onStreamSelected(stream)
    closeDropdown()
  }

  return (
    <ConfirmDialog
      message={confirmText}
      labelOK={<FormattedMessage id="actions.merge-streams" />}
      btnOKClassName={hideConfirmBtn ? styles.hidden : undefined}
    >
      {(confirm) => (
        <React.Fragment>
          <Button className={className} data-testid="merge-streams-select-btn" onClick={openDropdown}>
            <FormattedMessage id="actions.merge" />
          </Button>
          <Popper
            open={Boolean(anchor)}
            anchorEl={anchor}
            className={styles.popper}
            modifiers={overflowModifiers}
          >
            <ClickAwayListener onClickAway={closeDropdown}>
              <Paper className={styles['list-container']} elevation={5}>
                <ul>
                  <li className={styles.item}>
                    <FormattedMessage id="find.my-streams.manage.placeholder-select-merge-stream" />
                  </li>
                  {streams.map(s => (
                    <StreamListItem
                      key={s.id}
                      stream={s}
                      onBeforeClick={onBeforeConfirm}
                      onClick={confirm(onStreamClick)}
                    />
                  ))}
                </ul>
              </Paper>
            </ClickAwayListener>
          </Popper>
        </React.Fragment>
      )}
    </ConfirmDialog>
  )
}

const StreamListItem = (
  { stream, onClick, onBeforeClick }: { stream: Stream, onBeforeClick: (s: Stream) => void, onClick: (s: Stream) => void }
) => {
  const onClickHandler = () => {
    onBeforeClick(stream)
    onClick(stream)
  }

  return (
    <li className={styles.item} title={stream.title} data-testid="stream-list-item" onClick={onClickHandler}>
      <span className="text-ellipsis">{stream.title}</span>
    </li>
  )
}
